export default {
  pricing: {
    badge: 'Straightforward pricing',
    title: 'One hub or many. Pay for what you run.',
    subtitle: 'Geofleet Lite is purpose-built for single-hub delivery. Geofleet Command starts at $19 per driver for multi-hub and complex logistics operations.',
    currencyLabel: 'Show prices in',
    currencyBilledTag: 'billed',
    currencyNote: 'Prices are set and billed in USD. {{rate}} is an indicative conversion for reference.',
    featuresLabel: 'What\'s included',
    perMonth: '/month',
    perDriverMonth: '/driver/mo',
    includedDrivers: '{{count}} drivers included',
    includedOrders: '{{count}} orders/month included',
    ordersAtMinimum: '{{count}} orders/month at the 10-driver minimum',
    singleHubOnly: 'One hub only',
    minDrivers: '10-driver minimum',
    fromMonthly: 'From {{amount}}/month',
    lite: {
      eyebrow: 'Geofleet Lite',
      title: 'Simple delivery operations for growing teams',
      subtitle: 'For shops, kitchens, pharmacies and warehouses dispatching their own drivers from one pickup point.',
      boundary: 'Every Lite plan supports exactly one hub. Need a second location? Move to Geofleet Command.',
      billing: {
        label: 'Billing cycle',
        monthly: 'Monthly',
        quarterly: 'Quarterly',
        annual: 'Annual',
        save: 'Save {{percent}}%',
        saveUpTo: 'Save up to {{percent}}%',
        billedMonthly: 'Billed monthly',
        billedQuarterly: 'Billed {{amount}} every 3 months',
        billedAnnual: 'Billed {{amount}} per year',
      },
      extras: {
        hubs: 'Hubs', oneHub: 'One', extraDriver: 'Extra driver',
        extraOrder: 'Extra order',
        staffSeats: 'Staff seats',
        autopilot: 'AI Copilot & Autopilot',
        perDriverMonth: '{{amount}}/month',
        perOrder: '{{amount}} each',
        unlimited: 'Unlimited',
        notIncludedLabel: 'Not included',
      },
      trial: 'Every Lite plan starts with a 7-day free trial.',
      footnote: 'Prices in USD, before tax. Included drivers and orders reset every month on every billing cycle. Extra drivers and orders are billed monthly. SMS, WhatsApp and email updates are charged per message from your wallet.',
    },
    platform: {
      eyebrow: 'Geofleet Command',
      title: 'AI-run logistics, configured to your network',
      subtitle: 'For delivery companies, fleet operators, carriers and 3PLs that need AI dispatch, configurable workflows, multi-hub control and deep integrations. Priced per driver.',
      starting: 'Starts at $190/month with 10 drivers included',
      details: {
        minDrivers: 'Drivers included',
        ordersPerDriver: 'Orders per driver / month',
        ordersAtMinimum: 'Orders included / month',
        hubs: 'Hubs',
        multiple: 'Multiple',
        multiRegion: 'Multi-region',
        minimumLine: '{{count}} drivers included · extra drivers {{amount}}/month',
        quotedLine: 'Quoted for your network',
      },
    },
    assurances: {
      transparent: { title: 'Order limits up front', text: 'Every plan shows its included monthly order volume. No surprise usage threshold.' },
      oneHub: { title: 'A clear product boundary', text: 'Lite is exclusively for one hub. Multi-hub operations run on Geofleet Command.' },
      support: { title: 'Room to grow', text: 'Move from Lite to Geofleet when you add locations, advanced automation or carrier workflows.' },
    },
    plans: {
      lite: {
        name: 'Lite', badge: 'Essentials', tagline: 'For a small team running local deliveries', price: '$49', period: '/month',
        description: 'Includes 3 drivers and 500 orders each month.', cta: 'Start with Lite', autopilotMode: 'Not included',
        tableHighlights: ['Orders & inventory', 'Live tracking', 'Proof of delivery'],
        features: ['3 drivers included', '500 orders/month', 'Order management & CSV import', 'Inventory management', 'Driver app & live tracking', 'White-label tracking page', 'Proof of delivery & cash on delivery', 'Reporting'],
        notIncluded: ['Route planning & optimisation', 'Auto-allocation', 'WhatsApp automation', 'API & webhooks', 'AI Copilot & Autopilot'],
      },
      litePlus: {
        name: 'Lite Plus', badge: 'Planned & connected', highlight: 'Most value', tagline: 'For busy single-hub teams with daily routes', price: '$99', period: '/month',
        description: 'Includes 10 drivers and 1,000 orders each month.', cta: 'Start with Lite Plus', autopilotMode: 'Not included',
        tableHighlights: ['Route planning', 'Auto-allocation', 'WhatsApp automation'],
        features: ['Everything in Lite', '10 drivers included', '1,000 orders/month', 'Route planning & optimisation', 'Auto-allocation for on-demand orders', 'WhatsApp ordering & automated updates', 'Customers reschedule or cancel on WhatsApp', 'Shopify & WooCommerce sync', 'API & webhooks'],
        notIncluded: ['AI Copilot & Autopilot'],
      },
      liteAutopilot: {
        name: 'Lite Autopilot', badge: 'Hands-free dispatch', tagline: 'For high-volume hubs that want dispatch to run itself', price: '$179', period: '/month',
        description: 'Includes 25 drivers and 2,000 orders each month.', cta: 'Start with Lite Autopilot', autopilotMode: 'Included',
        tableHighlights: ['AI Copilot', 'Autopilot', 'Unlimited seats'],
        features: ['Everything in Lite Plus', '25 drivers included', '2,000 orders/month', 'AI Copilot answers questions and suggests fixes', 'Autopilot allocates and dispatches on its own', 'Unlimited staff seats'],
      },
      professional: {
        name: 'Professional', tagline: 'For growing, automated operations', price: '$19', period: '/driver/mo',
        description: 'Includes 5,000 orders each month. Each extra driver adds 500 orders.', cta: 'Get started', popular: 'Most popular',
        tableHighlights: ['Multi-hub', 'Route optimization', 'API & webhooks'],
        features: ['10 drivers included', '5,000 orders/month included', 'Multi-hub operations', 'Shopify & WooCommerce', 'Autopilot & auto-allocation', 'Route optimization', 'SMS/WhatsApp notifications', 'API & webhooks'],
      },
      premium: {
        name: 'Premium AI', tagline: 'For branded, AI-assisted operations', price: '$29', period: '/driver/mo',
        description: 'Includes 10,000 orders each month. Each extra driver adds 1,000 orders.', cta: 'Get started',
        tableHighlights: ['Branded apps', 'AI auto-dispatch', 'Barcode & ID'],
        features: ['Everything in Professional', '10 drivers included', '10,000 orders/month included', 'Branded tracking pages', 'Branded driver app', 'Barcode scanning & ID verification', 'AI delivery insights', 'AI auto-dispatch & allocation', '24/7 chat support'],
      },
      enterprise: {
        name: 'Enterprise', tagline: 'For large networks, carriers and 3PLs', price: 'Custom', period: '',
        description: 'Contracted order volume and a solution designed around your network.', cta: 'Contact sales',
        tableHighlights: ['AI agent suite', 'Custom reporting', 'Dedicated onboarding'],
        features: ['Everything in Premium AI', 'Contracted order allowance', 'Unlimited dispatchers', 'Full AI agent suite', 'Geo-fencing & advanced notifications', 'Custom reporting', 'Multi-location command centre', 'Dedicated account manager'],
      },
    },
    unified: {
      eyebrow: 'One clear rate card',
      title: 'Choose the plan that fits your operation',
      subtitle: 'Lite, Lite Plus and Lite Autopilot are exclusively for one hub. Professional, Premium AI and Enterprise make up Geofleet Command.',
      scrollHint: 'Swipe to view every plan detail',
      productFit: 'Product fit',
      liteGroup: 'Geofleet Lite',
      liteGroupNote: 'one hub only, billed at a flat monthly price',
      platformGroup: 'Geofleet Command',
      platformGroupNote: 'multi-hub operations, priced per driver',
      custom: 'Custom',
      included: '{{count}} included',
      minimum: '{{count}} minimum',
      orders: '{{count}} included',
      ordersAtMinimumShort: '{{count}} at minimum',
      contracted: 'Contracted',
      columns: {
        plan: 'Plan',
        bestFor: 'Best for',
        drivers: 'Drivers',
        orders: 'Orders / month',
        highlights: 'Included highlights',
        price: 'Price',
      },
      customVolume: 'Contracted order volume',
      customRollout: 'Custom rollout',
      groups: [
        {
          name: 'Plan basics',
          rows: [
            { feature: 'Billing price', values: ['$49/month', '$99/month', '$19/driver/month', '$29/driver/month', 'Custom'] },
            { feature: 'Drivers', values: ['3 included', '10 included', '10 minimum', '10 minimum', 'Custom'] },
            { feature: 'Orders per month', values: ['500', '1,000', '5,000 at minimum', '10,000 at minimum', 'Contracted'] },
            { feature: 'Hubs', values: ['One', 'One', 'Multiple', 'Multiple', 'Multi-region'] },
          ],
        },
        {
          name: 'Delivery operations',
          rows: [
            { feature: 'Driver mobile app', values: ['yes', 'yes', 'yes', 'yes', 'yes'] },
            { feature: 'Route planning and optimization', values: ['Planning', 'Optimized', 'Optimized', 'Dynamic', 'Custom'] },
            { feature: 'Live tracking and customer ETAs', values: ['yes', 'yes', 'yes', 'yes', 'yes'] },
            { feature: 'Proof of delivery and COD', values: ['yes', 'yes', 'yes', 'yes', 'yes'] },
          ],
        },
        {
          name: 'Integrations, automation and AI',
          rows: [
            { feature: 'Shopify and WooCommerce', values: ['no', 'yes', 'yes', 'yes', 'yes'] },
            { feature: 'Autopilot and auto-allocation', values: ['no', 'yes', 'yes', 'yes', 'yes'] },
            { feature: 'SMS and WhatsApp updates', values: ['no', 'yes', 'yes', 'yes', 'yes'] },
            { feature: 'API and webhooks', values: ['no', 'no', 'yes', 'yes', 'yes'] },
            { feature: 'Branded customer experience', values: ['Customer link', 'Branded', 'Branded', 'Full brand', 'Custom'] },
            { feature: 'AI auto-dispatch', values: ['no', 'no', 'no', 'yes', 'yes'] },
            { feature: 'Full AI agent suite', values: ['no', 'no', 'no', 'no', 'yes'] },
          ],
        },
      ],
    },
    estimator: {
      title: 'Estimate your Geofleet Command cost',
      subtitle: 'Move the slider to your active driver count. Geofleet Command plans start at 10 drivers.',
      drivers: 'Drivers', driversValue: '{{count}} drivers', perMonth: '/month', ordersIncluded: '{{count}} orders included',
      note: 'Order allowances scale with active drivers. SMS, WhatsApp and third-party carrier charges are billed separately.',
      talk: 'Talk to us',
    },
    compare: {
      title: 'Compare Geofleet Command plans',
      subtitle: 'Every Command plan runs multiple hubs on AI dispatch you can configure. Choose by volume, automation and AI depth.',
      feature: 'Feature', included: 'Included', notIncluded: 'Not included',
      groups: [
        { name: 'Plan basics', rows: [
            { feature: 'Price per driver per month', values: ['$19', '$29', 'Custom'] },
            { feature: 'Minimum drivers', values: ['10', '10', 'Custom'] },
            { feature: 'Orders included per driver', values: ['500', '1,000', 'Contracted'] },
            { feature: 'Orders at 10 drivers', values: ['5,000', '10,000', 'Contracted'] },
            { feature: 'Hubs', values: ['Multiple', 'Multiple', 'Multi-region'] },
        ] },
        { name: 'Delivery operations', rows: [
          { feature: 'Driver mobile app', values: ['yes', 'yes', 'yes'] },
          { feature: 'Live tracking and customer ETAs', values: ['yes', 'yes', 'yes'] },
          { feature: 'Route planning and optimization', values: ['Optimized', 'Dynamic', 'Custom'] },
          { feature: 'Proof of delivery and COD', values: ['yes', 'yes', 'yes'] },
          { feature: 'Shopify and WooCommerce', values: ['yes', 'yes', 'yes'] },
          { feature: 'API and webhooks', values: ['yes', 'yes', 'yes'] },
        ] },
        { name: 'Automation, brand and AI', rows: [
          { feature: 'Autopilot and auto-allocation', values: ['yes', 'yes', 'yes'] },
          { feature: 'Branded tracking and driver app', values: ['Tracking page', 'yes', 'yes'] },
          { feature: 'Barcode scanning and ID verification', values: ['no', 'yes', 'yes'] },
          { feature: 'AI auto-dispatch and delivery insights', values: ['no', 'yes', 'yes'] },
          { feature: 'Full AI agent suite and geo-fencing', values: ['no', 'no', 'yes'] },
        ] },
      ],
    },
    faqTitle: 'Frequently asked questions',
    faqs: [
      { question: 'Is Geofleet Lite only for one hub?', answer: 'Yes. Lite, Lite Plus and Lite Autopilot are purpose-built for businesses that dispatch from exactly one shop, kitchen, pharmacy or warehouse. If you add a second hub, move to Geofleet Command.' },
      { question: 'How much does Geofleet Lite cost?', answer: 'Lite is $49 per month with 3 drivers and 500 orders. Lite Plus is $99 per month with 10 drivers and 1,000 orders. Lite Autopilot is $179 per month with 25 drivers and 2,000 orders. Quarterly billing saves about 10% and annual billing saves up to 20%, so Lite starts at $39 per month billed annually.' },
      { question: 'How is Geofleet Command priced?', answer: 'Professional starts at $190 per month with 10 drivers and 5,000 orders included, and each extra driver is $19 per month. Premium AI is $29 per driver per month with 10,000 orders at the minimum.' },
      { question: 'Does Geofleet connect to Shopify and WooCommerce?', answer: 'Yes. Lite Plus, Lite Autopilot and Geofleet Command Professional, Premium AI and Enterprise include Shopify and WooCommerce integrations. API and webhooks are included from Lite Plus to connect other order sources.' },
      { question: 'What is Autopilot?', answer: 'Autopilot automates routine dispatch work such as allocating waiting orders, preparing routes and handling repeatable delivery workflows. On Geofleet Lite, Autopilot and AI Copilot are included in Lite Autopilot. On Geofleet Command, Autopilot is included from Professional.' },
      { question: 'Is there a free plan?', answer: 'No. You can try Geofleet Lite for 7 days with up to 100 orders before choosing a plan.' },
    ],
    ctaTitle: 'Choose the product built for your operation',
    ctaSubtitle: 'Start with Lite for one hub, or talk to us about a multi-hub Geofleet rollout.',
    ctaStart: 'Start with Lite', ctaSales: 'Talk to sales',
  },
};
